import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { 
  Heart, 
  TrendingUp, 
  BookOpen, 
  Sparkles,
  ArrowRight,
  X
} from 'lucide-react';

const WelcomePopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      // Wait until the user has finished the required first-sign-in disclaimer flow.
      const disclaimersDone = localStorage.getItem(`cp_disclaimers_complete_${user.id}`);
      if (!disclaimersDone) {
        // Re-check shortly — the disclaimer flow may still be in progress.
        const recheck = setInterval(() => {
          if (localStorage.getItem(`cp_disclaimers_complete_${user.id}`)) {
            clearInterval(recheck);
            const hasSeenWelcome = localStorage.getItem(`welcome_shown_${user.id}`);
            if (!hasSeenWelcome) setIsOpen(true);
          }
        }, 800);
        return () => clearInterval(recheck);
      }

      // Check if user has seen the welcome popup
      const hasSeenWelcome = localStorage.getItem(`welcome_shown_${user.id}`);
      
      if (!hasSeenWelcome) {
        // Small delay to let the dashboard load first
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  const handleClose = () => {
    if (user) {
      localStorage.setItem(`welcome_shown_${user.id}`, 'true');
    }
    setIsOpen(false);
  };

  const handleGoToGuide = () => {
    if (user) {
      localStorage.setItem(`welcome_shown_${user.id}`, 'true');
    }
    setIsOpen(false);
    navigate('/how-to-use');
  };

  const handleExplore = () => {
    if (user) {
      localStorage.setItem(`welcome_shown_${user.id}`, 'true');
    }
    setIsOpen(false);
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="welcome-popup">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6 relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            data-testid="welcome-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-8 h-8 fill-white" />
            <DialogTitle className="text-2xl font-nunito font-bold text-white">
              Welcome to Calm Pathways!
            </DialogTitle>
          </div>
          <DialogDescription className="text-white/90 text-base">
            We're so glad you're here. Let us show you how to get the most out of your journey.
          </DialogDescription>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Key Feature Highlight */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-[#2D2D2D] mb-1">
                  Discover Your #1 Tool: Timeline & Insights
                </h3>
                <p className="text-sm text-gray-600">
                  Track symptoms, food, sleep & more — then watch as our AI finds patterns 
                  and correlations you'd never spot on your own.
                </p>
              </div>
            </div>
          </div>

          {/* Quick benefits */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
              <span>AI-powered pattern detection across all your data</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
              <span>Visual graphs to track progress over time</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
              <span>Shareable reports for your care team</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={handleGoToGuide}
              className="flex-1 bg-primary text-white hover:bg-primary/90 h-12"
              data-testid="welcome-guide-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Show Me How to Use It
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={handleExplore}
              variant="outline"
              className="flex-1 h-12"
              data-testid="welcome-explore-btn"
            >
              I'll Explore On My Own
            </Button>
          </div>

          {/* Future help reminder */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <div className="flex items-start gap-2">
              <BookOpen className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                <strong>Need help later?</strong> You can always access the complete guide by clicking 
                <span className="font-semibold"> "How to Use"</span> in the sidebar menu. We're here whenever you need us!
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomePopup;
